import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { apiCall } from "../_services/apiCall";
import config from "../config/config";
import { getLatestFY } from "../component/DataManagment/Quantative/component/answerHistoryPage/components/utils";

// Global financial-year filter shared across the whole dashboard (Progress,
// Environment, Training, custom graphs, …). The selected year is persisted so it
// survives navigation and reloads; the default is the latest FY (getLatestFY).
const STORAGE_KEY = "globalFinancialYearId";
const LIST_CACHE_KEY = "financialYearData"; // reuse the existing FY-list cache

const FinancialYearContext = createContext(null);

// Coerce a raw select/value into a numeric FY id (or "" when cleared).
const toFYId = (val) =>
  val === "" || val === null || val === undefined || Number.isNaN(Number(val))
    ? ""
    : Number(val);

export const FinancialYearProvider = ({ children }) => {
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFYId, setSelectedFYIdState] = useState(() =>
    toFYId(localStorage.getItem(STORAGE_KEY))
  );
  const [loading, setLoading] = useState(true);

  const latestFY = useMemo(() => getLatestFY(financialYears), [financialYears]);

  const setSelectedFYId = useCallback((val) => {
    const id = toFYId(val);
    setSelectedFYIdState(id);
    if (id === "") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(id));
  }, []);

  useEffect(() => {
    let active = true;

    const load = async () => {
      let list = null;

      const cached = localStorage.getItem(LIST_CACHE_KEY);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length) list = parsed;
        } catch {
          list = null;
        }
      }

      if (!list) {
        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getFinancialYear`,
          {},
          {},
          "GET"
        );
        if (isSuccess && Array.isArray(data?.data)) {
          list = data.data;
          localStorage.setItem(LIST_CACHE_KEY, JSON.stringify(list));
        }
      }

      if (!active) return;

      list = list || [];
      setFinancialYears(list);

      // Choose the active FY: a previously persisted one if still valid,
      // otherwise the latest financial year.
      const storedId = toFYId(localStorage.getItem(STORAGE_KEY));
      const storedValid =
        storedId !== "" && list.some((fy) => Number(fy.id) === storedId);

      if (storedValid) {
        setSelectedFYIdState(storedId);
      } else {
        const latest = getLatestFY(list);
        if (latest) {
          setSelectedFYIdState(Number(latest.id));
          localStorage.setItem(STORAGE_KEY, String(latest.id));
        }
      }

      setLoading(false);
    };

    load().catch((err) => {
      console.error("Failed to load financial years:", err);
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(
    () => ({ financialYears, selectedFYId, setSelectedFYId, latestFY, loading }),
    [financialYears, selectedFYId, setSelectedFYId, latestFY, loading]
  );

  return (
    <FinancialYearContext.Provider value={value}>
      {children}
    </FinancialYearContext.Provider>
  );
};

export const useFinancialYear = () => {
  const ctx = useContext(FinancialYearContext);
  if (!ctx) {
    throw new Error(
      "useFinancialYear must be used within a FinancialYearProvider"
    );
  }
  return ctx;
};
